import StructuredData from '@atoms/StructuredData/StructuredData';
import { VACANCY_PLACEHOLDER_IMAGE } from '@config/vacancies/placeholder';
import { VacancyPageLayout } from '@layouts/VacancyPage/VacancyPage';
import getOrCreateCustomerId from '@lib/customer';
import fetchApi from '@lib/fetchApi';
import getConfigProps from '@lib/getConfigProps';
import { getFieldOfWork, getLocationDetails } from '@lib/prepr';
import { vacancyPageType } from '@lib/proptypes';
import { isCorporateDomain } from '@utils/getDomain';
import { getJobPostingStructuredData } from '@utils/getStructuredData';

function VacancyAggregationPage({ page }) {
  const schemas = page.aggregationChildren.map(child => {
    const locationDetails = page.aggLocationDetails[child.locationCode] || {};
    return getJobPostingStructuredData({
      ...page,
      ...child,
      salaryScale: {
        minSalary: child.minSalary,
        maxSalary: child.maxSalary,
      },
      location: {
        code: child.locationCode,
      },
      locationDetails,
    });
  });

  return (
    <>
      <VacancyPageLayout page={page} />
      {schemas.map(schema => (
        <StructuredData key={schema.identifier.value} schema={schema} />
      ))}
    </>
  );
}

VacancyAggregationPage.propTypes = {
  page: vacancyPageType,
};

export async function getServerSideProps(ctx) {
  const { req, res, params, preview = false, query } = ctx;
  const { host } = req.headers;
  const { slug } = params;
  const customerId = getOrCreateCustomerId(req);

  if (isCorporateDomain(host)) {
    return {
      notFound: true,
    };
  }

  const filterVariables = {
    applicationForms: query.applicationForms
      ? query.applicationForms?.split(',')
      : [],
    functionCategories: query.functionCategories
      ? query.functionCategories?.split(',')
      : [],
    locations: query.locations ? query.locations?.split(',') : [],
    educationLevels: query.educationLevels
      ? query.educationLevels?.split(',')
      : [],
    minHours: query.minHours,
    maxHours: query.maxHours,
  };

  const { data } = await fetchApi({
    method: 'post',
    path: `/vacancies/filtered-children/${slug}`,
    variables: {
      ...filterVariables,
    },
  })
    .then(response => response)
    .catch(e => {
      console.error(e);
      return { error: e };
    });

  if (!data) {
    return {
      notFound: true,
    };
  }

  const [
    {
      fieldOfWork,
      storyPages,
      recruiter: {
        items: [recruiter],
      },
      applicationProcess,
    },
    {
      props: { config },
    },
  ] = await Promise.all([
    getFieldOfWork({
      host,
      preview,
      functionCategoryCode: data.functionCategory?.code,
      recruiterCode: data.recruiter?.code,
      applicationFormCode: data.applicationForm?.code,
    }),
    getConfigProps(ctx),
  ]);

  const headerImage = fieldOfWork?.headerImages?.length
    ? fieldOfWork.headerImages[data.headerImageIndex]
    : VACANCY_PLACEHOLDER_IMAGE;

  const uniqueLocationCodes = [
    ...new Set(data.aggregationChildren.map(child => child.locationCode)),
  ];
  let aggLocationDetails = {};
  await Promise.allSettled(
    uniqueLocationCodes.map(locationCode =>
      getLocationDetails({ host, afasCode: locationCode })
    )
  ).then(
    // Filter out the failed requests and return the value of the fulfilled requests.
    responses =>
      responses
        .filter(
          ({ status, value }) => status === 'fulfilled' && value?.items[0]
        )
        .forEach(({ value }) => {
          aggLocationDetails = {
            ...aggLocationDetails,
            [value.items[0].afasCode]: { ...value.items[0] },
          };
        })
  );

  return {
    props: {
      page: {
        metaTitle: `${data.title} - Vitalis`,
        metaDescription: data.shortDescription,
        ogImage: headerImage,
        typename: 'VacancyPage',
        ...data,
        headerImage,
        fieldOfWork: fieldOfWork || null,
        storyPages: storyPages || null,
        applicationProcess: applicationProcess || null,
        aggLocationDetails: aggLocationDetails || null,
        recruiter: recruiter || null,
      },
      preview,
      config,
      host,
      customerId,
    },
  };
}

export default VacancyAggregationPage;
